// Small presentational pieces of the processing console shell.
import React from "react";
import { ShieldCheck } from "lucide-react";

export { ExtractionFeed } from "./ExtractionFeed";

export function Panel({
  title,
  meta,
  children,
  grow,
  noHeader,
}: {
  title?: string;
  meta?: string;
  children: React.ReactNode;
  grow?: boolean;
  noHeader?: boolean;
}) {
  return (
    <section
      className={`relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm ${
        grow ? "flex-1" : ""
      }`}
    >
      {!noHeader && (
        <>
          <div className="flex items-center justify-between gap-2 px-4 pb-2.5 pt-3">
            <span className="flex items-center gap-2 text-[11px] font-semibold tracking-wide text-muted-foreground">
              <span className="size-1.5 flex-none rounded-full bg-indigo-500" />
              {title}
            </span>
            {meta && (
              <span className="flex-none text-[11px] font-medium text-muted-foreground">
                {meta}
              </span>
            )}
          </div>
          <div className="h-px bg-border" />
        </>
      )}
      {children}
    </section>
  );
}

export function ConsoleLogo() {
  return (
    <span className="grid size-9 flex-none place-items-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200/60 dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-500/20">
      <ShieldCheck className="size-5" />
    </span>
  );
}

// Keyframes the console needs that Tailwind does not ship.
export function ConsoleKeyframes() {
  return (
    <style>{`
@keyframes rgc-blink { 50% { opacity: .4 } }
@keyframes rgc-ring { 0% { transform: scale(1); opacity: .7 } 100% { transform: scale(1.7); opacity: 0 } }
@keyframes rgc-slidein { from { opacity: 0; transform: translateX(14px) } to { opacity: 1; transform: translateX(0) } }
@keyframes rgc-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.06) } }
@keyframes rgc-pulse-out {
  0%   { transform: scale(.9);  opacity: .5 }
  70%  { opacity: .12 }
  100% { transform: scale(2.1); opacity: 0 }
}
@keyframes rgc-spin { to { transform: rotate(360deg) } }
@keyframes rgc-spin-rev { to { transform: rotate(-360deg) } }
@keyframes rgc-travel {
  0%   { transform: translateX(0) translateY(-50%); opacity: 0 }
  12%  { opacity: 1 }
  80%  { opacity: 1 }
  100% { transform: translateX(var(--rgc-reach, 120px)) translateY(-50%); opacity: 0 }
}
@keyframes rgc-drift { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-5px) } }
@keyframes rgc-shimmer { 0%,100% { opacity: .55 } 50% { opacity: .9 } }
@keyframes rgc-fail-pop {
  0%   { opacity: 0; transform: translate(-50%, 6px) scale(.85) }
  55%  { opacity: 1; transform: translate(-50%, 0) scale(1.06) }
  100% { opacity: 1; transform: translate(-50%, 0) scale(1) }
}
@keyframes rgc-fail-out {
  0%   { opacity: 1; transform: translate(-50%, 0) scale(1) }
  100% { opacity: 0; transform: translate(-50%, -4px) scale(.96) }
}
@keyframes rgc-fail-shake {
  0%, 92%, 100% { transform: translateX(0) }
  93% { transform: translateX(-1.5px) }
  94% { transform: translateX(1.5px) }
  95% { transform: translateX(-1.5px) }
  96% { transform: translateX(1.5px) }
  97% { transform: translateX(-1px) }
  98% { transform: translateX(1px) }
}
@keyframes rgc-broken-jitter {
  0%, 100% { transform: scale(1); opacity: .85 }
  50% { transform: scale(1.35); opacity: 1 }
}
@keyframes rgc-indeterminate {
  0%   { transform: translateX(-100%) }
  100% { transform: translateX(300%) }
}
@media (prefers-reduced-motion: reduce) {
  [style*="rgc-"] { animation-duration: .001ms !important; animation-iteration-count: 1 !important }
}
`}</style>
  );
}
